/**
 * THE gesture maths behind swiping between list tabs on a phone (My Orders,
 * 2026-09-30). Pure functions only — no React, no DOM writes — so every rule
 * is testable in jsdom without a finger. `hooks/useSwipeTabs.js` wires them to
 * touch events and `components/ui/Tabs.js`' `snapCenter` uses
 * `nearestTabIndexToCentre` for the strip half.
 *
 * The owner's rules, each one a function below:
 *   1. A HORIZONTAL-DOMINANT swipe moves to the next / previous tab
 *      (`lockAxis` + `classifySwipe`).
 *   2. VERTICAL SCROLL ALWAYS WINS — the axis is decided ONCE, on the first
 *      movement past `AXIS_LOCK_PX`, and a gesture that locks vertical can
 *      never become a tab change however far it later drifts sideways.
 *   3. A small horizontal JITTER never changes tab (`SWIPE_COMMIT_PX`, and the
 *      flick path still needs `SWIPE_FLICK_MIN_PX`).
 *   4. Scrolling INSIDE a horizontal scroller (the order table) is the
 *      scroller's, never the tab strip's — unless the scroller was ALREADY at
 *      the edge the swipe pushes against when the finger went down
 *      (`findHorizontalScroller` + `scrollEdgeState` + `scrollerBlocksSwipe`).
 *      🔑 The edge is read at TOUCH START, not at release: a swipe that scrolls
 *      the table to its edge and keeps going is still a table scroll.
 *   5. `prefersReducedMotion()` — the caller switches instantly, no follow and
 *      no slide.
 *
 * 🪤 **Direction is the finger's, mirrored like a carousel**: finger moves
 * LEFT (`dx < 0`) → the NEXT tab; right → the previous. That is also the
 * direction the content of a horizontal scroller moves, which is what makes
 * the edge rule line up: `next` is blocked while the scroller can still
 * scroll RIGHT.
 */

/** Movement (px) before the axis is decided. Below it, nothing happens. */
export const AXIS_LOCK_PX = 10;

/**
 * How much more horizontal than vertical the first movement must be to lock
 * the X axis. 1.2 rather than 1: a diagonal is a scroll, because a wrongly
 * taken tab change costs the user their place and a wrongly taken scroll
 * costs nothing.
 */
export const HORIZONTAL_DOMINANCE = 1.2;

/** Horizontal travel (px) that commits a slow, deliberate swipe. */
export const SWIPE_COMMIT_PX = 56;

/** A FLICK commits on less travel, but never on jitter. */
export const SWIPE_FLICK_MIN_PX = 30;
export const SWIPE_FLICK_VELOCITY = 0.5; // px per ms

/** Visual follow: damped and capped, so the list only hints at the move. */
export const FOLLOW_DAMPING = 0.35;
export const FOLLOW_MAX_PX = 72;

/**
 * Decide the axis of a gesture from its travel so far.
 * @returns {'x'|'y'|null} null = not yet moved far enough to tell.
 */
export function lockAxis(dx, dy) {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (Math.max(ax, ay) < AXIS_LOCK_PX) return null;
  return ax > ay * HORIZONTAL_DOMINANCE ? 'x' : 'y';
}

/**
 * The verdict at finger-up for a gesture that locked the X axis.
 * @returns {'next'|'prev'|null}
 */
export function classifySwipe({ dx, dy = 0, dtMs = Infinity }) {
  const ax = Math.abs(dx);
  if (!(ax > Math.abs(dy))) return null;
  const velocity = dtMs > 0 ? ax / dtMs : 0;
  const committed = ax >= SWIPE_COMMIT_PX
    || (ax >= SWIPE_FLICK_MIN_PX && velocity >= SWIPE_FLICK_VELOCITY);
  if (!committed) return null;
  return dx < 0 ? 'next' : 'prev';
}

/** The damped translate the list shows while a locked swipe is in flight. */
export function followOffset(dx) {
  const v = dx * FOLLOW_DAMPING;
  return Math.max(-FOLLOW_MAX_PX, Math.min(FOLLOW_MAX_PX, v));
}

/**
 * The neighbouring tab in a direction — skipping disabled tabs and NOT
 * wrapping (a swipe past the last tab does nothing, like a carousel; the
 * keyboard's wraparound is a different, deliberate contract in `ui/Tabs`).
 * @returns {string|null}
 */
export function adjacentTabId(tabs, activeId, direction) {
  const i = tabs.findIndex((t) => t.id === activeId);
  if (i < 0) return null;
  const step = direction === 'next' ? 1 : -1;
  for (let j = i + step; j >= 0 && j < tabs.length; j += step) {
    if (!tabs[j].disabled) return tabs[j].id;
  }
  return null;
}

/**
 * Index of the rect whose horizontal centre is closest to `centreX`.
 * @param {number} centreX
 * @param {{left:number,width:number}[]} rects
 * @returns {number} -1 for an empty list.
 */
export function nearestTabIndexToCentre(centreX, rects) {
  let best = -1;
  let bestDist = Infinity;
  rects.forEach((r, i) => {
    if (!r) return;
    const d = Math.abs(r.left + r.width / 2 - centreX);
    if (d < bestDist) { bestDist = d; best = i; }
  });
  return best;
}

function isXScrollable(el) {
  if (!el || el.nodeType !== 1) return false;
  if (!(el.scrollWidth > el.clientWidth + 1)) return false;
  // The page-scroll table's panes carry this marker (`ui/PageScrollTable`);
  // anything else is recognised by its computed overflow.
  if (el.hasAttribute('data-page-scroll-pane')) return true;
  try {
    const ox = window.getComputedStyle(el).overflowX;
    return ox === 'auto' || ox === 'scroll';
  } catch (e) {
    return false;
  }
}

/**
 * The nearest horizontally scrollable ancestor of `target`, stopping at (and
 * excluding) `boundary` — the swipe area itself.
 */
export function findHorizontalScroller(target, boundary) {
  let el = target && target.nodeType === 1 ? target : target && target.parentElement;
  while (el && el !== boundary) {
    if (isXScrollable(el)) return el;
    el = el.parentElement;
  }
  return null;
}

/** Can this scroller still move left / right? Read at touch start. */
export function scrollEdgeState(el) {
  if (!el) return { canScrollLeft: false, canScrollRight: false };
  const left = el.scrollLeft;
  return {
    canScrollLeft: left > 1,
    canScrollRight: left + el.clientWidth < el.scrollWidth - 1,
  };
}

/**
 * Does the scroller the finger started in own this swipe? `next` pushes the
 * content right-to-left (it wants to scroll RIGHT); `prev` the reverse.
 */
export function scrollerBlocksSwipe(direction, edgeAtStart) {
  if (!edgeAtStart) return false;
  return direction === 'next' ? edgeAtStart.canScrollRight : edgeAtStart.canScrollLeft;
}

/** Form controls keep their own horizontal gestures (caret, selection). */
export function isSwipeExcludedTarget(target) {
  const el = target && target.nodeType === 1 ? target : target && target.parentElement;
  if (!el || typeof el.closest !== 'function') return false;
  return Boolean(el.closest('input, textarea, select, [contenteditable="true"], [data-swipe-ignore]'));
}

export function prefersReducedMotion() {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
}
